import { createPortal } from 'react-dom';
import { X, Download, FileText } from 'lucide-react';
import type { ChatAttachmentMeta } from '../../lib/chat';
import { humanSize, isPdf } from './attachmentUtils';

/**
 * Full preview of a chat attachment — image, video, or PDF (rendered in an
 * iframe). Other document types can't be previewed in-browser, so we offer a
 * download instead. Shared by the chat message and the "Media & docs" gallery.
 */
export function FilePreviewModal({
  meta,
  onClose,
}: {
  meta: ChatAttachmentMeta;
  onClose: () => void;
}) {
  const previewable = meta.media === 'image' || meta.media === 'video' || isPdf(meta);

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-neutral-100 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <FileText className="size-4 shrink-0 text-neutral-400" />
            <span className="truncate text-sm font-medium text-neutral-800">{meta.filename}</span>
            <span className="shrink-0 text-xs text-neutral-400">{humanSize(meta.bytes)}</span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <a
              href={meta.url}
              target="_blank"
              rel="noopener noreferrer"
              download={meta.filename}
              className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
            >
              <Download className="size-4" /> Download
            </a>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close preview"
              className="rounded-full p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto bg-neutral-50">
          {meta.media === 'image' ? (
            <img src={meta.url} alt={meta.filename} className="mx-auto max-h-[80vh] w-auto" />
          ) : meta.media === 'video' ? (
            <video src={meta.url} controls autoPlay className="mx-auto max-h-[80vh] w-full bg-black" />
          ) : isPdf(meta) ? (
            <iframe src={meta.url} title={meta.filename} className="h-[80vh] w-full" />
          ) : (
            <div className="flex flex-col items-center gap-3 px-6 py-16 text-center text-neutral-500">
              <FileText className="size-10 text-neutral-300" />
              <p className="text-sm">This file type can't be previewed in the browser.</p>
              <a
                href={meta.url}
                target="_blank"
                rel="noopener noreferrer"
                download={meta.filename}
                className="inline-flex items-center gap-1.5 rounded-lg bg-pink-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-pink-600"
              >
                <Download className="size-4" /> Download to view
              </a>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
